import { arrayBufferToBase64 } from '../../common/utils';
import { logger } from '../Logger';
import type { DocumentContent } from './types';

const log = logger.createContextLogger('PowerPointService');

export async function insertIntoPowerPoint(documentContent: DocumentContent): Promise<void> {
  try {
    if (!documentContent.binaryContent) {
      throw new Error('No binary content available for insertion');
    }

    const arrayBuffer = await documentContent.binaryContent.arrayBuffer();
    const textDecoder = new TextDecoder();
    const textContent = textDecoder.decode(arrayBuffer);

    if (
      documentContent.contentType === 'text/plain' ||
      textContent.includes('demo document generated')
    ) {
      log.debug('Inserting demo PowerPoint slides with welcome info');

      if (typeof PowerPoint !== 'undefined' && PowerPoint.run) {
        await PowerPoint.run(async (context) => {
          const slides = context.presentation.slides;
          slides.load('items');
          await context.sync();

          // SlideCollection.add() returns void, not a Slide proxy. Add the three
          // demo slides first, then reload the collection and address the newly
          // created slides by index. Adding before deleting also guarantees the
          // presentation is never momentarily empty (PowerPoint requires >= 1 slide).
          const originalCount = slides.items.length;
          context.presentation.slides.add();
          context.presentation.slides.add();
          context.presentation.slides.add();
          await context.sync();

          const allSlides = context.presentation.slides;
          allSlides.load('items');
          await context.sync();

          const slide1 = allSlides.items[originalCount];
          const slide2 = allSlides.items[originalCount + 1];
          const slide3 = allSlides.items[originalCount + 2];

          // New slides inherit the layout's placeholder shapes ("Click to add
          // title", "Click to add text"). Remove them so only our content shows.
          const demoSlides = [slide1, slide2, slide3];
          for (const demoSlide of demoSlides) {
            demoSlide.shapes.load('items');
          }
          await context.sync();
          for (const demoSlide of demoSlides) {
            const placeholders = demoSlide.shapes.items;
            for (let index = placeholders.length - 1; index >= 0; index--) {
              placeholders[index].delete();
            }
          }
          await context.sync();

          const titleShape = slide1.shapes.addTextBox('Welcome to iVALT DocuID', {
            left: 50,
            top: 50,
            width: 600,
            height: 70,
          });
          titleShape.textFrame.textRange.font.size = 40;
          titleShape.textFrame.textRange.font.bold = true;

          slide1.shapes.addTextBox('Biometric-Secured Document Management\nfor Microsoft Office', {
            left: 50,
            top: 130,
            width: 600,
            height: 60,
          }).textFrame.textRange.font.size = 20;

          slide1.shapes.addTextBox(
            'Access, manage, and share your documents securely with\none-tap biometric authentication via the iVALT mobile app.',
            { left: 50, top: 210, width: 600, height: 50 },
          ).textFrame.textRange.font.size = 15;

          const footerShape = slide1.shapes.addTextBox('iVALT DocuID', {
            left: 50,
            top: 290,
            width: 600,
            height: 30,
          });
          footerShape.textFrame.textRange.font.size = 12;
          footerShape.textFrame.textRange.font.color = '#807d72';

          const featTitle = slide2.shapes.addTextBox('Key Features', {
            left: 50,
            top: 30,
            width: 600,
            height: 60,
          });
          featTitle.textFrame.textRange.font.size = 32;
          featTitle.textFrame.textRange.font.bold = true;

          slide2.shapes.addTextBox(
            '  Biometric document access with one-tap login\n' +
              '  Secure sharing with full audit trail\n' +
              '  Works across Word, Excel, and PowerPoint\n' +
              '  Real-time document activity tracking\n' +
              '  End-to-end encrypted file storage',
            { left: 50, top: 110, width: 600, height: 200 },
          ).textFrame.textRange.font.size = 18;

          const howTitle = slide3.shapes.addTextBox('How It Works', {
            left: 50,
            top: 30,
            width: 600,
            height: 60,
          });
          howTitle.textFrame.textRange.font.size = 32;
          howTitle.textFrame.textRange.font.bold = true;

          slide3.shapes.addTextBox(
            '1. Login with your registered mobile number\n' +
              '2. Approve the biometric request on the iVALT app\n' +
              '3. Browse and open documents from the task pane\n' +
              '4. Share documents securely with colleagues',
            { left: 50, top: 110, width: 600, height: 180 },
          ).textFrame.textRange.font.size = 18;

          // Remove the slides that existed before the demo content was added.
          for (let index = originalCount - 1; index >= 0; index--) {
            allSlides.items[index].delete();
          }

          await context.sync();
        });
      }
    } else {
      // For real pptx files use insertSlidesFromBase64 (if available)
      log.debug('Inserting base64 PPTX content into PowerPoint');
      const base64String = arrayBufferToBase64(arrayBuffer);
      if (typeof PowerPoint !== 'undefined' && PowerPoint.run) {
        await PowerPoint.run(async (context) => {
          context.presentation.insertSlidesFromBase64(base64String);
          await context.sync();
        });
      } else {
        throw new Error('PowerPoint.run API required for document insertion');
      }
    }

    log.logOfficeOperation('Document insertion (PowerPoint)', true);
  } catch (error) {
    log.logOfficeOperation('Document insertion (PowerPoint)', false, error);
    throw error;
  }
}

export async function clearPowerPointDocument(): Promise<void> {
  if (typeof PowerPoint !== 'undefined' && PowerPoint.run) {
    try {
      await PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load('items');
        await context.sync();

        if (slides.items.length > 0) {
          for (let index = slides.items.length - 1; index >= 0; index--) {
            slides.items[index].delete();
          }
          context.presentation.slides.add();
        }
        await context.sync();
      });
    } catch {
      log.info('PowerPoint.run error while clearing slides (non-fatal)');
    }
  } else {
    log.info('PowerPoint.run not available for clearing slides');
  }
}
